import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import OnboardingLogo from "../../components/onboarding/OnboardingLogo";
import { onboardingShell, card, primaryBtn } from "../../components/onboarding/styles";
import { API_BASE } from "../../lib/api";

function maskEmail(email) {
  if (!email || !email.includes("@")) return email || "";
  const [local, domain] = email.split("@");
  const head = local.slice(0, 2);
  return `${head}****@${domain}`;
}

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [email] = useState(() => sessionStorage.getItem("onboarding_email") || "");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    if (!email) {
      navigate("/signup", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (canResend) return undefined;
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(id);
          setCanResend(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [canResend]);

  const handleChange = (idx) => (e) => {
    const ch = (e.target.value || "").slice(-1);
    if (ch && !/[0-9]/.test(ch)) return;
    setOtp((arr) => {
      const next = [...arr];
      next[idx] = ch;
      return next;
    });
    if (ch && idx < 5) refs[idx + 1].current?.focus();
  };

  const handleKeyDown = (idx) => (e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      refs[idx - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    const data = (e.clipboardData?.getData("text") || "").trim();
    if (/^\d{6}$/.test(data)) {
      e.preventDefault();
      setOtp(data.split(""));
      refs[5].current?.focus();
    }
  };

  const onVerify = async (e) => {
    e?.preventDefault?.();
    setError("");
    const code = otp.join("");
    if (code.length < 6) {
      setError("Please enter the complete 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/onboarding/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp_code: code }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.name) sessionStorage.setItem("onboarding_name", data.name);
        navigate("/business-profile");
        return;
      }
      let detail = "";
      try { const j = await res.json(); detail = j?.detail || ""; } catch (_e) {}
      if (detail === "incorrect_code") {
        setError("Incorrect code. Please try again.");
        setOtp(["", "", "", "", "", ""]);
        refs[0].current?.focus();
      } else if (detail === "no_valid_otp") {
        setError("This code has expired. Click Resend Code to get a new one.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (_err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setError("");
    setInfo("");
    setCanResend(false);
    setCountdown(60);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/onboarding/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setInfo("Code resent — check your inbox.");
        setTimeout(() => setInfo(""), 3000);
      } else {
        setError("Could not resend the code right now. Please try again.");
        setCanResend(true);
      }
    } catch (_err) {
      setError("Could not resend the code right now. Please try again.");
      setCanResend(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="verify-page" style={onboardingShell}>
      <div style={{ marginBottom: 28 }}><OnboardingLogo /></div>

      <div style={{ ...card, width: 420, maxWidth: "100%", padding: 40 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <div style={{ width: 56, height: 56, background: "#ECFDF5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Mail size={28} color="#059669" />
          </div>
        </div>

        <h2 style={{ color: "#1E3A5F", fontSize: 22, fontWeight: 700, textAlign: "center", margin: 0 }}>Check your email</h2>
        <p style={{ color: "#6B7280", fontSize: 14, textAlign: "center", marginTop: 8, marginBottom: 0 }}>We sent a 6-digit code to</p>
        <p data-testid="verify-masked-email" style={{ color: "#1E3A5F", fontWeight: 700, fontSize: 14, textAlign: "center", marginTop: 4, marginBottom: 0 }}>{maskEmail(email)}</p>
        <p style={{ color: "#9CA3AF", fontSize: 12, textAlign: "center", marginTop: 6 }}>Check your inbox and spam folder. Code expires in 10 minutes.</p>

        <form onSubmit={onVerify}>
          <div style={{ display: "flex", justifyContent: "center", gap: 8, margin: "28px 0" }}>
            {otp.map((v, i) => (
              <input
                key={i}
                data-testid={`verify-otp-${i}`}
                ref={refs[i]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={v}
                onChange={handleChange(i)}
                onKeyDown={handleKeyDown(i)}
                onPaste={i === 0 ? handlePaste : undefined}
                style={{
                  width: 48, height: 56, border: "1px solid #D1D5DB",
                  borderRadius: 8, textAlign: "center", fontSize: 26,
                  fontWeight: 700, color: "#1E3A5F", outline: "none",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = "#059669"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(5,150,105,0.12)"; }}
                onBlur={(e)  => { e.currentTarget.style.borderColor = "#D1D5DB"; e.currentTarget.style.boxShadow = "none"; }}
              />
            ))}
          </div>

          {error && (
            <p data-testid="verify-error" style={{ color: "#DC2626", fontSize: 12, textAlign: "center", marginTop: -16, marginBottom: 8 }}>{error}</p>
          )}
          {info && (
            <p data-testid="verify-info" style={{ color: "#059669", fontSize: 12, textAlign: "center", marginTop: -16, marginBottom: 8 }}>{info}</p>
          )}

          <button
            data-testid="verify-submit"
            type="submit"
            disabled={loading}
            style={{
              ...primaryBtn,
              background: loading ? "#10b981" : "#059669",
              opacity: loading ? 0.85 : 1,
              cursor: loading ? "default" : "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "#047857"; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = "#059669"; }}
          >
            {loading && (
              <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.5)", borderTopColor: "white", borderRadius: "50%", display: "inline-block", animation: "core-spin 0.7s linear infinite" }} />
            )}
            {loading ? "Verifying…" : "Verify Code"}
          </button>
        </form>

        <p style={{ textAlign: "center", fontSize: 13, color: "#6B7280", marginTop: 16 }}>
          Did not receive an email?{" "}
          {canResend ? (
            <button
              data-testid="verify-resend"
              type="button"
              onClick={onResend}
              style={{ color: "#059669", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
            >
              Resend Code
            </button>
          ) : (
            <span data-testid="verify-countdown" style={{ color: "#9CA3AF" }}>Resend available in {countdown}s</span>
          )}
        </p>

        <p style={{ textAlign: "center", fontSize: 12, color: "#9CA3AF", marginTop: 8 }}>
          Wrong email?{" "}
          <span
            data-testid="verify-go-back"
            onClick={() => navigate("/signup")}
            style={{ color: "#6B7280", cursor: "pointer", textDecoration: "underline" }}
          >
            Go back
          </span>
        </p>
      </div>

      <style>{`@keyframes core-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
